import type { AnomalyDetail, WeldAnalyticsBundle, WeldSummary } from '../types/weld';
import type { OverviewWeldRow } from '../components/WeldOverviewTable';
import type {
  DurationDrillDownFilter,
  NdtBadgeStatus,
  NdtIndicationRow,
  WeldDrillDownRow,
} from '../components/dashboard/drilldown/drillDownTypes';
import type { DashboardMetrics, NormalizedWeld, WeldOutcome } from './weldDashboardMetrics';

function formatJobNumber(jn: string[] | string | undefined): string {
  if (!jn) return '';
  if (Array.isArray(jn)) return jn[0] ?? '';
  return jn;
}

function weldKey(weldNumber: string, jobNumber: string): string {
  return `${jobNumber}::${weldNumber}`;
}

function findProductionRow(
  productionRows: OverviewWeldRow[],
  weldNumber: string,
  jobNumber: string,
): OverviewWeldRow | null {
  return (
    productionRows.find((row) => {
      const rowJob = formatJobNumber(row.job_number);
      const rowWeld = String(row.weldNumber ?? '');
      return rowWeld === weldNumber && (!jobNumber || !rowJob || rowJob === jobNumber);
    }) ?? null
  );
}

function findWeldSummary(
  bundle: WeldAnalyticsBundle | null,
  weldNumber: string,
  jobNumber: string,
): WeldSummary | null {
  if (!bundle?.welds?.length) return null;
  return (
    bundle.welds.find(
      (w) => w.weld_number === weldNumber && (!jobNumber || w.job_number === jobNumber),
    ) ?? null
  );
}

export function anomaliesForWeld(
  bundle: WeldAnalyticsBundle | null,
  weldNumber: string,
  jobNumber: string,
): AnomalyDetail[] {
  if (!bundle?.anomalies?.length) return [];
  return bundle.anomalies.filter(
    (a) => a.weld_number === weldNumber && (!jobNumber || a.job_number === jobNumber),
  );
}

export function ndtBadgeFromLogs(logs: NdtIndicationRow[] | null | undefined): NdtBadgeStatus {
  if (!logs?.length) return 'none';
  const hasRejected = logs.some((log) => {
    const evalText = String(log.evaluation ?? '').toLowerCase();
    return evalText.includes('reject') || evalText === 'fail' || evalText === 'failed';
  });
  if (hasRejected) return 'rejected';
  return 'has_records';
}

export function outcomeLabel(outcome: WeldOutcome): string {
  if (outcome === 'pass') return 'Within limit';
  if (outcome === 'fail') return 'Out of limit';
  return 'Other';
}

export function filterByOutcome(
  rows: WeldDrillDownRow[],
  filter: 'pass' | 'fail' | 'all',
): WeldDrillDownRow[] {
  if (filter === 'all') return rows;
  return rows.filter((row) => row.outcome === filter);
}

const DURATION_EXACT_TOLERANCE_SEC = 0.05;

export function filterByDurationBin(
  rows: WeldDrillDownRow[],
  loSec: number,
  hiSec: number | null,
): WeldDrillDownRow[] {
  return rows.filter((row) => {
    const d = row.durationSec;
    if (d == null || !Number.isFinite(d) || d <= 0) return false;
    if (hiSec == null) return d >= loSec;
    if (hiSec === loSec) return Math.abs(d - loSec) <= DURATION_EXACT_TOLERANCE_SEC;
    return d >= loSec && d < hiSec;
  });
}

export function filterByExactDuration(
  rows: WeldDrillDownRow[],
  durationSec: number,
): WeldDrillDownRow[] {
  return rows.filter((row) => {
    const d = row.durationSec;
    if (d == null || !Number.isFinite(d)) return false;
    return Math.abs(d - durationSec) <= DURATION_EXACT_TOLERANCE_SEC;
  });
}

export function filterByWeldIdentity(
  rows: WeldDrillDownRow[],
  weldNumber: string,
  jobNumber: string,
): WeldDrillDownRow[] {
  return rows.filter(
    (row) =>
      row.weldNumber === weldNumber && (!jobNumber || !row.jobNumber || row.jobNumber === jobNumber),
  );
}

export function applyDurationDrillDownFilter(
  rows: WeldDrillDownRow[],
  filter: DurationDrillDownFilter,
): WeldDrillDownRow[] {
  if (filter.type === 'exact') {
    return filterByExactDuration(rows, filter.durationSec);
  }
  if (filter.type === 'weld') {
    return filterByWeldIdentity(rows, filter.weldNumber, filter.jobNumber);
  }
  return filterByDurationBin(rows, filter.loSec, filter.hiSec);
}

export function buildWeldDrillDownRows(
  metrics: DashboardMetrics,
  productionRows: OverviewWeldRow[],
  bundle: WeldAnalyticsBundle | null,
  fallbackProjectId?: number | null,
): WeldDrillDownRow[] {
  return metrics.welds.map((weld: NormalizedWeld) => {
    const productionRow = findProductionRow(productionRows, weld.weldNumber, weld.jobNumber);
    const weldSummary = findWeldSummary(bundle, weld.weldNumber, weld.jobNumber);
    const anomalies = anomaliesForWeld(bundle, weld.weldNumber, weld.jobNumber);

    const prod = productionRow as OverviewWeldRow & {
      wpsNumber?: string;
      projectId?: number;
      projectName?: string;
      pipe_diameter?: string | number;
      wall_thickness?: string | number;
    } | null;

    return {
      weldNumber: weld.weldNumber,
      jobNumber: weld.jobNumber,
      wpsNumber: prod?.wpsNumber ?? '',
      outcome: weld.outcome,
      durationSec: weld.durationSec,
      startTimeMs: weld.startTimeMs,
      endTimeMs: weld.endTimeMs,
      qualityScore: weld.qualityScore,
      pipeDiameter: prod?.pipe_diameter ?? null,
      wallThickness: prod?.wall_thickness ?? null,
      projectId: prod?.projectId ?? fallbackProjectId ?? null,
      projectName: prod?.projectName ?? '',
      statusByInstantaneous: prod?.statusByInstantaneous ?? null,
      statusByAvgZone: prod?.statusByAvgZone ?? null,
      statusByAvgPass: prod?.statusByAvgPass ?? null,
      statusByAvgTilt: prod?.statusByAvgTilt ?? null,
      weldSummary,
      anomalies,
      productionRow,
      ndtBadge: 'none',
      ndtLogCount: null,
    };
  });
}

export function rowKey(row: WeldDrillDownRow): string {
  return weldKey(row.weldNumber, row.jobNumber);
}

export function complianceCounts(rows: WeldDrillDownRow[]): {
  within: number;
  out: number;
  other: number;
  ratePct: number | null;
} {
  const within = rows.filter((r) => r.outcome === 'pass').length;
  const out = rows.filter((r) => r.outcome === 'fail').length;
  const other = rows.filter((r) => r.outcome === 'other').length;
  const total = within + out;
  const ratePct = total > 0 ? (within / total) * 100 : null;
  return { within, out, other, ratePct };
}
